import React from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';
import { Partner } from 'src/reducers/partner/types';

import { getPartnerTranslation, getPartnerTranslationKey } from 'Utilities/helpers';
import mediator from 'Utilities/mediator';

import {
  ERROR_PARTNERS_ACCEPT_CONSENT,
  ERROR_PARTNERS_DECLINE_CONSENT,
} from 'Constants/errorTypes';
import { PARTNER_TYPES } from 'Constants/partnerTypes';
import { SCOPE_TYPE_READ, SCOPE_TYPE_WRITE } from 'Constants/scopeTypes';

import ScopeSection from 'Components/consent/ScopeSection';
import HtmlTranslation from 'Components/HtmlTranslation';

const mapState = ({
  config: { privacyPolicy, termsOfUse },
  user: { code, yearOfBirth, phoneLanguage },
}: RootState) => {
  return {
    code,
    yearOfBirth,
    abbottPrivacy:
      (privacyPolicy && (privacyPolicy[phoneLanguage] || privacyPolicy['default'])) || '',
    abbottTOS: (termsOfUse && (termsOfUse[phoneLanguage] || termsOfUse['default'])) || '',
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector> & {
  partner: Partner;
  code: string;
  yearOfBirth: number;
};

type ScopeMap = Record<string, string[]>;

const declineConsent = (code: string, partnerId: string, yearOfBirth: number) => {
  mediator
    .request('partner:declineConsent', {
      code,
      yearOfBirth,
      partnerId,
    })
    .catch((err) => {
      mediator.publish('error:display', {
        error: {
          code: err.status,
          type: ERROR_PARTNERS_DECLINE_CONSENT,
        },
      });
    });
};

const acceptConsent = (code: string, partnerId: string, yearOfBirth: number) => {
  mediator
    .request('partner:acceptConsent', {
      code,
      yearOfBirth,
      partnerId,
    })
    .catch((err) => {
      mediator.publish('router:navigate', '/');

      mediator.publish('error:display', {
        error: {
          code: err.status,
          type: ERROR_PARTNERS_ACCEPT_CONSENT,
        },
      });
    });
};

const createScopeMap = (partner: Partner): ScopeMap => {
  const scopeMap: ScopeMap = {};

  scopeMap[SCOPE_TYPE_READ] = [];
  scopeMap[SCOPE_TYPE_WRITE] = [];

  if (partner.type === PARTNER_TYPES.COMMERCIAL_API) {
    // This may change in the future when requirements are firmed up, but for now we assume that all scopes
    // configured for the partner will be requested from the patient

    partner.scopes.forEach((scope) => {
      const type = scope.split('.')[0];

      scope.startsWith(type) && scopeMap[type].push(scope);
    });
  }

  return scopeMap;
};

const ManageConsent: React.FC<React.PropsWithChildren<Props>> = ({
  abbottPrivacy,
  abbottTOS,
  code,
  partner,
  yearOfBirth,
}) => {
  if (partner.type !== PARTNER_TYPES.COMMERCIAL_API) {
    return null;
  }

  const scopeMap = createScopeMap(partner);

  if (scopeMap[SCOPE_TYPE_READ].length === 0 && scopeMap[SCOPE_TYPE_WRITE].length === 0) {
    return null;
  }

  return (
    <div>
      <div className="mb-3">
        <header className="d-flex justify-content-between align-items-center">
          <div className="d-flex">
            <span className="text-xl text-black">
              {getPartnerTranslation('Consent', partner.type, 'confirmSharing')}
            </span>
          </div>
        </header>
      </div>

      <ScopeSection
        scopeType={SCOPE_TYPE_READ}
        scopes={scopeMap[SCOPE_TYPE_READ]}
        partnerName={partner.name}
        partnerType={partner.type}
        scopeDetails={partner.scopeDetails}
      />

      {scopeMap[SCOPE_TYPE_READ].length > 0 && <hr />}

      <ScopeSection
        scopeType={SCOPE_TYPE_WRITE}
        scopes={scopeMap[SCOPE_TYPE_WRITE]}
        partnerName={partner.name}
        partnerType={partner.type}
        scopeDetails={partner.scopeDetails}
      />

      {scopeMap[SCOPE_TYPE_WRITE].length > 0 && <hr />}

      {!partner.hideDisclaimer && (
        <HtmlTranslation
          id="consentForm-privacy-text"
          className="mb-3 text-black-50 d-block"
          translationKey={getPartnerTranslationKey('Consent', partner.type, 'accessDisclaimer')}
          translationProps={{
            partnerName: partner.name,
          }}
        />
      )}

      <HtmlTranslation
        id="consentForm-privacy-text"
        className="mb-3 text-black-50 d-block"
        translationKey={getPartnerTranslationKey('Consent', partner.type, 'privacyStatement')}
        translationProps={{
          partnerName: partner.name,
          abbottPrivacy,
          abbottTOS,
        }}
      />

      <div className="row justify-content-between">
        <div className="col-6 col-sm-4 pr-2">
          <button
            type="button"
            className="btn btn-secondary text-lg font-weight-bold w-100"
            onClick={() => declineConsent(code, partner.id, yearOfBirth)}
          >
            {getPartnerTranslation('Common', partner.type, 'decline_cap')}
          </button>
        </div>
        <div className="col-6 col-sm-4 pl-2">
          <button
            type="button"
            className="btn btn-primary text-lg font-weight-bold w-100"
            onClick={() => acceptConsent(code, partner.id, yearOfBirth)}
          >
            {getPartnerTranslation('Common', partner.type, 'allow_cap')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default connector(ManageConsent);
